"use client"

import type { ReactNode } from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"
import { Languages, Plug, UserPlus, Users } from "lucide-react"
import type { HubCounts } from "@/hooks/use-hub-counts"
import { useTranslation } from "@/hooks/use-translation"
import { formatNumber } from "@/lib/locale"
import type { Language } from "@/lib/locale"
import type { TranslationKey } from "@/translations"

export interface HubTabDefinition {
  value: string
  labelKey: TranslationKey
  /** Per-card accent bar, in the navy-to-slate ramp the Hub screens share. */
  accent: string
  icon: ReactNode
}

export const HUB_TABS: HubTabDefinition[] = [
  { value: "migrations", labelKey: "tabs.workers", accent: "#0f214b", icon: <Users className="h-3.5 w-3.5" /> },
  { value: "providers", labelKey: "tabs.providers", accent: "#3f4d6f", icon: <Plug className="h-3.5 w-3.5" /> },
  { value: "mappings", labelKey: "tabs.mappings", accent: "#6b768f", icon: <Languages className="h-3.5 w-3.5" /> },
  { value: "backfill", labelKey: "tabs.backfill", accent: "#4aa06d", icon: <UserPlus className="h-3.5 w-3.5" /> },
]

type Translate = (key: TranslationKey, vars?: Record<string, string | number>) => string

/**
 * The headline figure and caption on one card. A null count means the figure
 * could not be fetched, and shows as "—" — never as a zero, which would read as
 * "nothing to do here" when the truth is that we could not ask.
 */
function summaryFor(
  tab: string,
  counts: HubCounts,
  t: Translate,
  language: Language
): { value: string; sub: string } {
  const dash = "—"
  switch (tab) {
    case "migrations":
      return {
        value: counts.workers === null ? dash : formatNumber(counts.workers, language),
        sub: t("hub.workers.sub"),
      }
    case "providers": {
      if (counts.providerName === null) return { value: dash, sub: t("hub.providers.noneYet") }
      if (counts.providerName === "") return { value: t("providers.notSetUp"), sub: t("hub.providers.noneYet") }
      const env = (counts.providerEnvironment ?? "").toLowerCase()
      return {
        value: counts.providerName,
        sub: env === "" ? t("hub.providers.noneYet") : t(env.startsWith("prod") ? "common.env.production" : "common.env.sandbox"),
      }
    }
    case "mappings":
      return {
        value: counts.valueSets === null ? dash : formatNumber(counts.valueSets, language),
        sub: t("hub.mappings.sub"),
      }
    case "backfill":
      return {
        value:
          counts.candidates === null
            ? dash
            : formatNumber(counts.candidates, language) + (counts.candidatesHasMore ? "+" : ""),
        sub: t("hub.backfill.sub"),
      }
    default:
      return { value: dash, sub: "" }
  }
}

/**
 * The hub's tools as summary cards, matching the ATS Hub: white cards on the
 * canvas, a per-card accent bar, an uppercase label, the figure and a caption;
 * the open one takes the navy edge and an accent underline.
 *
 * Radix tab semantics are kept (roving focus, arrow keys, aria-selected) by
 * using the primitives directly — the app's own TabsTrigger paints the small
 * GW pill tab, which is a different control entirely.
 */
export function HubTabs({ counts }: { counts: HubCounts }) {
  const { t, language } = useTranslation()

  return (
    <TabsPrimitive.List
      aria-label={t("hub.tablistAria")}
      className="grid grid-cols-2 gap-3 md:grid-cols-4"
    >
      {HUB_TABS.map((tab) => {
        const summary = summaryFor(tab.value, counts, t, language)
        return (
          <TabsPrimitive.Trigger
            key={tab.value}
            value={tab.value}
            className="group relative flex flex-col items-start gap-0.5 overflow-hidden rounded-[10px] border border-[var(--th-card-border)] bg-white px-3 py-2.5 text-left shadow-[var(--th-card-shadow)] transition-all hover:border-[var(--th-chip-border)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=active]:border-[var(--color-primary)] data-[state=active]:ring-1 data-[state=active]:ring-[var(--color-primary)]"
          >
            <span className="absolute inset-y-0 left-0 w-1" style={{ background: tab.accent }} aria-hidden="true" />
            <span className="th-section-label flex items-center gap-1.5">
              <span style={{ color: tab.accent }} aria-hidden="true">
                {tab.icon}
              </span>
              {t(tab.labelKey)}
            </span>
            <span className="font-heading text-lg font-extrabold leading-tight text-foreground">{summary.value}</span>
            <span className="w-full truncate text-[11px] text-muted-foreground">{summary.sub}</span>
            {/* bg-fbo-accent, not bg-[var(--fbo-accent)] — the variable holds a bare HSL triplet. */}
            <span
              className="absolute inset-x-0 bottom-0 hidden h-0.5 bg-fbo-accent group-data-[state=active]:block"
              aria-hidden="true"
            />
          </TabsPrimitive.Trigger>
        )
      })}
    </TabsPrimitive.List>
  )
}
